import {ReactComponent as InformationIcon} from "../../ui/icon/Information.svg";

import "./_sign-flow-waiting-for-sign-view.scss";

import {useEffect} from "react";
import {SwapType} from "@tinymanorg/tinyman-js-sdk";

import Typography from "../../../component/typography/Typography";
import {useTxnSign} from "../../../hook/useTxnSign";
import {useSwapFormState} from "../../../swap/form/context/SwapFormContext";
import {
  getSwapTypeFromFormState,
  calculateEstimatedSwapImpact
} from "../../../swap/form/SwapForm.utils";
import {SignFlowState, SignFlowStep} from "../SignFlow.types";
import useEffectOnce from "../../util/hook/useEffectOnce";
import {getAssetUnitDisplayName} from "../../../asset/util/assetUtils";

interface SignFlowWaitingForSignViewProps {
  setSignFlowState: React.Dispatch<React.SetStateAction<SignFlowState>>;
}

function SignFlowWaitingForSignView({setSignFlowState}: SignFlowWaitingForSignViewProps) {
  const {runSign, signerState} = useTxnSign();
  const state = useSwapFormState();
  const swapType = getSwapTypeFromFormState(state);
  const isFixedInput = swapType === SwapType.FixedInput;
  const swapImpact =
    state.quote &&
    calculateEstimatedSwapImpact({
      state,
      quote: state.quote
    });

  useEffectOnce(() => {
    if (state.txnsToSign) {
      runSign([state.txnsToSign]);
    } else {
      setSignFlowState({step: SignFlowStep.Error, error: "No transactions to sign."});
    }
  }, [runSign, state.txnsToSign]);

  useEffect(() => {
    if (signerState.status === "success") {
      setSignFlowState({
        step: SignFlowStep.SendingTxn,
        signedTxns: signerState.signedTxns
      });
    } else if (signerState.status === "error") {
      setSignFlowState({
        step: SignFlowStep.Error,
        error: signerState.error || new Error("Error while signing txn")
      });
    }
  }, [setSignFlowState, signerState]);

  return (
    <div className={"is-centered sign-flow-waiting-for-sign-view"}>
      <div
        className={
          "is-centered sign-flow-waiting-for-sign-view__information-icon-container"
        }>
        <InformationIcon />
      </div>

      <Typography
        as={"h2"}
        tabIndex={0}
        variant={"title"}
        customClassName={"sign-flow-waiting-for-sign-view__title"}>
        {"Please sign the transaction"}
      </Typography>

      <Typography
        tabIndex={0}
        customClassName={"sign-flow-waiting-for-sign-view__description"}
        textColor={"text-gray"}>
        {isFixedInput ? "You will receive at least " : "You will spend at most "}

        <Typography
          as={"span"}
          variant={"body"}
          fontWeight={"medium"}
          textColor={"text-gray"}>
          {isFixedInput ? swapImpact?.minimumReceived : swapImpact?.maximumSent}
        </Typography>

        <Typography
          as={"span"}
          variant={"body"}
          fontWeight={"medium"}
          textColor={"text-gray"}>
          {isFixedInput
            ? ` ${getAssetUnitDisplayName(state.assetOut.asset)}`
            : ` ${getAssetUnitDisplayName(state.assetIn.asset)}`}
        </Typography>

        {" or the transaction will revert."}
      </Typography>
    </div>
  );
}

export default SignFlowWaitingForSignView;
